import { PIN_HEIGHT, PIN_WIDTH, PinInput, escapeHtml, cssUrl, renderTitle, requireImageUrl, brandInitials, PHOTO_GRADE } from "./types";

// venueOverlay — minimal "text floats on the photo" layout. No opaque card:
// the headline + subtitle + brand sit at the TOP over a soft top-down scrim,
// so the lower two-thirds of the photo (the subject — set tables, people,
// cars, a barn) stay fully visible. Used when the photo's busy content is in
// the lower portion and the top is open sky / ceiling. The scrim keeps white
// text legible over any top (clear sky, clouds, beams) while letting the
// image read through — far lighter than the flipped card box.
export function venueOverlayTemplate(input: PinInput): string {
  const { eyebrow, brand, brandLogo, cta, typography, venue } = input;
  const imageUrl = requireImageUrl(input, "venueOverlay");
  if (!venue) {
    throw new Error("venueOverlay template requires venue { name, location?, tagline? }");
  }

  // Length→size ladder tuned so the headline fits TWO lines at the ~888px
  // overlay width (left/right inset 56). Client titles cap at 44 chars
  // (MAX_TITLE_CHARS), so the longest bucket still lands on 2 lines.
  const nameLen = venue.name.length;
  const nameSize = nameLen <= 22 ? 104 : nameLen <= 32 ? 92 : nameLen <= 42 ? 78 : 68;

  // No-logo monogram: custom text or auto initials, serif or script.
  const monoText = (input.monogramText && input.monogramText.trim()) || brandInitials(brand);
  const monoClass = input.monogramFont === "serif" ? "serif" : "script";

  // Full-color logos (wax seals, crests) skip the monochrome whitening and
  // keep just the drop-shadow for legibility over the photo.
  const logoFilter = input.logoFullColor
    ? "drop-shadow(0 2px 8px rgba(0,0,0,.5))"
    : "brightness(0) invert(1) drop-shadow(0 2px 8px rgba(0,0,0,.5))";

  return `<!doctype html>
<html><head>
<meta charset="utf-8" />
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link rel="stylesheet" href="${typography.googleFontsHref}" />
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap" />
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  html,body{width:${PIN_WIDTH}px;height:${PIN_HEIGHT}px;font-family:${typography.bodyFont};-webkit-font-smoothing:antialiased;background:#000}
  .pin{position:relative;width:100%;height:100%;overflow:hidden}
  .image{position:absolute;inset:0;background-image:url('${cssUrl(imageUrl)}');background-size:cover;background-position:${Math.round((input.focusX ?? 0.5) * 100)}% ${Math.round((input.focusY ?? 0.5) * 100)}%;filter:${input.rawPhoto ? "none" : PHOTO_GRADE}}
  /* Directional scrim: a diagonal darken anchored at the TOP-LEFT corner
     (where the headline sits) fading out by ~55% so the center/right of the
     photo stays bright — plus a thin bottom strip so the logo + CTA footer
     stays legible. Keeps the image the hero; text reads without a heavy box. */
  .image::after{content:"";position:absolute;inset:0;background:linear-gradient(135deg,rgba(0,0,0,.70) 0%,rgba(0,0,0,.42) 32%,rgba(0,0,0,.08) 62%),linear-gradient(0deg,rgba(0,0,0,.60) 0%,rgba(0,0,0,0) 22%)}
  .overlay{position:absolute;top:64px;left:56px;right:56px;z-index:2;color:#fff}
  ${eyebrow ? `.eyebrow{font-size:30px;letter-spacing:5px;text-transform:uppercase;font-weight:700;opacity:.94;margin-bottom:22px;text-shadow:0 2px 12px rgba(0,0,0,.45)}` : ""}
  .name{font-family:${typography.headlineFont};font-size:${nameSize}px;font-weight:${typography.headlineWeight};line-height:1.12;letter-spacing:-1.5px;margin-bottom:26px;padding-bottom:.16em;text-shadow:0 2px 18px rgba(0,0,0,.5);display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
  .name em{font-style:italic;font-weight:400}
  .subtitle{font-size:50px;line-height:1.28;opacity:.97;max-width:88%;padding-bottom:.16em;text-shadow:0 2px 14px rgba(0,0,0,.45);display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
  /* Footer pinned to the bottom: logo on top, CTA centered BELOW it (stacked),
     so a wide wordmark logo never pushes the CTA off the edge. */
  .footer{position:absolute;bottom:56px;left:56px;right:56px;z-index:2;display:flex;flex-direction:column;align-items:center;gap:18px;color:#fff;text-align:center}
  .brand{font-weight:800;letter-spacing:3px;text-transform:uppercase;font-size:28px;text-shadow:0 2px 10px rgba(0,0,0,.5);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  /* No-logo fallback monogram. Serif = small ringed mark; script = floating
     signature (no ring). */
  .monogram{flex-shrink:0;color:#fff;display:flex;align-items:center;justify-content:center;text-shadow:0 2px 8px rgba(0,0,0,.5)}
  .monogram.serif{width:74px;height:74px;border:2.5px solid rgba(255,255,255,.85);border-radius:50%;font-family:${typography.headlineFont};font-size:32px;letter-spacing:1px}
  .monogram.script{font-family:'Great Vibes',cursive;font-size:60px;line-height:1}
  /* max-height (not fixed height) + max-width so wide wordmarks scale down
     proportionally instead of squishing. ~74px tall for normal logos. */
  .brand-logo{max-height:219px;max-width:100%;width:auto;height:auto;display:block;filter:${logoFilter}}
  .cta{font-size:30px;font-weight:600;letter-spacing:.3px;white-space:nowrap;text-shadow:0 2px 10px rgba(0,0,0,.5)}
</style></head>
<body><div class="pin">
  <div class="image"></div>
  <div class="overlay">
    ${eyebrow ? `<div class="eyebrow">${escapeHtml(eyebrow)}</div>` : ""}
    <div class="name">${renderTitle(venue.name, input.titleAccent)}</div>
    ${venue.tagline ? `<div class="subtitle">${escapeHtml(venue.tagline)}</div>` : ""}
  </div>
  <div class="footer">
    ${
      input.hideLogo
        ? ""
        : brandLogo
          ? `<img class="brand-logo" src="${brandLogo}" alt="${escapeHtml(brand)}" />`
          : `<span class="monogram ${monoClass}">${escapeHtml(monoText)}</span>`
    }
    ${cta ? `<span class="cta">${escapeHtml(cta)} →</span>` : ""}
  </div>
</div></body></html>`;
}
